import { ContactDialog } from "@/components/contacts/ContactDialog";
import { PurchaseOrderSheet } from "@/components/operations";
import {
  buildInventoryWorkspacePath,
  formatStatusLabel,
  getPurchaseOrderDisplayStatus,
} from "@/components/operations/operations-shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { VendorDataSchema, type Vendor } from "@/core";
import {
  useAllVendors,
  useCreateVendor,
  useDeleteVendor,
  usePurchaseOrdersByVendorId,
  useUpdateVendor,
  useVendorById,
} from "@/hooks";
import { ContactFormValues } from "@/hooks/forms/entities/use-contact-form";
import { useUserStore } from "@/stores/user";
import { removeUndefinedValues } from "@/utils/remove-undefined-values";
import {
  formatTimestamp,
  getVendorDisplayName,
  getVendorSearchKeywords,
} from "@/utils";
import { AlertCircle, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const OPTIONAL_VENDOR_FIELDS = [
  "secondaryPhone",
  "secondaryEmail",
  "companyName",
] as const;

function getMissingVendorRequirements({ vendor }: { vendor: Vendor }) {
  return [
    !vendor.email?.trim() ? "Missing email" : null,
    !vendor.phone?.trim() ? "Missing phone" : null,
  ].filter((value): value is string => Boolean(value));
}

export default function VendorsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedVendorId = searchParams.get("vendorId") || "";
  const selectedPoId = searchParams.get("poId") || "";
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null);
  const { hasFullAccess, hasClientVendorWriteAccess } = useUserStore();

  const vendorsQuery = useAllVendors();
  const vendorQuery = useVendorById(selectedVendorId);
  const purchaseOrdersQuery = usePurchaseOrdersByVendorId(selectedVendorId);
  const createVendorMutation = useCreateVendor();
  const updateVendorMutation = useUpdateVendor();
  const deleteVendorMutation = useDeleteVendor();

  const vendors = useMemo(
    () => vendorsQuery.data?.pages.flat() ?? [],
    [vendorsQuery.data],
  );
  const filteredVendors = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return vendors;
    }

    return vendors.filter((vendor) =>
      getVendorSearchKeywords({ vendor }).some((value) =>
        value.toLowerCase().includes(normalizedSearch),
      ),
    );
  }, [searchTerm, vendors]);
  const purchaseOrders = purchaseOrdersQuery.data ?? [];
  const selectedVendor =
    vendors.find((vendor) => vendor.id === selectedVendorId) || vendorQuery.data;

  const vendorLabel = selectedVendor
    ? getVendorDisplayName({ vendor: selectedVendor })
    : "Select a vendor";

  const normalizeVendorFormValues = ({ data }: { data: ContactFormValues }) => ({
    ...data,
    secondaryPhone: data.secondaryPhone?.trim() || undefined,
    secondaryEmail: data.secondaryEmail?.trim() || undefined,
    companyName: data.companyName?.trim() || undefined,
    source: data.source || undefined,
  });

  const handleCreateVendor = async (data: ContactFormValues) => {
    const normalizedData = normalizeVendorFormValues({ data });
    const parsedData = VendorDataSchema.parse(normalizedData);
    const cleanData = removeUndefinedValues(parsedData);
    const vendorId = await createVendorMutation.mutateAsync({
      data: cleanData,
    });

    setIsCreateDialogOpen(false);
    setSearchParams({ vendorId });
  };

  const handleUpdateVendor = async (data: ContactFormValues) => {
    if (!editingVendor) {
      return;
    }

    const normalizedData = normalizeVendorFormValues({ data });
    const parsedData = VendorDataSchema.parse(normalizedData);
    const cleanData = removeUndefinedValues(parsedData);
    const updateData = { ...cleanData };

    for (const field of OPTIONAL_VENDOR_FIELDS) {
      if (editingVendor[field] && normalizedData[field] === undefined) {
        updateData[field] = "";
      }
    }

    await updateVendorMutation.mutateAsync({
      id: editingVendor.id,
      data: updateData,
    });
    setEditingVendor(null);
  };

  const handleDeleteVendor = async () => {
    if (!vendorToDelete) {
      return;
    }

    await deleteVendorMutation.mutateAsync({
      id: vendorToDelete.id,
    });

    if (selectedVendorId === vendorToDelete.id) {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete("vendorId");
        next.delete("poId");
        return next;
      });
    }

    setVendorToDelete(null);
  };

  return (
    <div className="container mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Vendors</CardTitle>
              <CardDescription>
                Manage vendors and inspect the purchase orders linked to them.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[16rem]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => vendorsQuery.refetch()}
                disabled={vendorsQuery.isFetching}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${vendorsQuery.isFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              {hasClientVendorWriteAccess() ? (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add vendor
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="w-[140px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendorsQuery.isLoading || vendorsQuery.hasNextPage ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    Loading vendors...
                  </TableCell>
                </TableRow>
              ) : filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No vendors found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map((vendor) => {
                  const displayName = getVendorDisplayName({ vendor });
                  const missingRequirements = getMissingVendorRequirements({ vendor });

                  return (
                    <TableRow
                      key={vendor.id}
                      className="cursor-pointer"
                      onClick={() => setSearchParams({ vendorId: vendor.id })}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{displayName}</span>
                          {missingRequirements.length > 0 ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <AlertCircle className="h-4 w-4 text-amber-500" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={6} className="text-black">
                                {missingRequirements.join(" and ")}
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div>{vendor.email || "-"}</div>
                          {vendor.secondaryEmail ? (
                            <div className="text-xs text-muted-foreground">
                              {vendor.secondaryEmail}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div>{vendor.phone || "-"}</div>
                          {vendor.secondaryPhone ? (
                            <div className="text-xs text-muted-foreground">
                              {vendor.secondaryPhone}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className="flex items-center gap-1"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {hasClientVendorWriteAccess() ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={() => setEditingVendor(vendor)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {hasFullAccess() ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8 text-destructive"
                              onClick={() => setVendorToDelete(vendor)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {!hasClientVendorWriteAccess() ? (
                            <span className="text-sm text-muted-foreground">View only</span>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{vendorLabel}</CardTitle>
          <CardDescription>
            Purchase orders associated with the selected vendor.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!selectedVendorId ? (
            <div className="text-sm text-muted-foreground">
              Select a vendor to view linked purchase orders.
            </div>
          ) : purchaseOrdersQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">
              Loading purchase orders...
            </div>
          ) : purchaseOrders.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No purchase orders are linked to this vendor yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Amount Bought</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchaseOrders.map((purchaseOrder) => (
                  <TableRow
                    key={purchaseOrder.id}
                    className="cursor-pointer"
                    onClick={() =>
                      setSearchParams({
                        vendorId: selectedVendorId,
                        poId: purchaseOrder.id,
                      })}
                  >
                    <TableCell>{purchaseOrder.productName}</TableCell>
                    <TableCell>{formatStatusLabel(getPurchaseOrderDisplayStatus(purchaseOrder))}</TableCell>
                    <TableCell>{formatStatusLabel(purchaseOrder.paymentStatus)}</TableCell>
                    <TableCell>{purchaseOrder.source}</TableCell>
                    <TableCell>{purchaseOrder.amountBought.toLocaleString()}</TableCell>
                    <TableCell>
                      {formatTimestamp(purchaseOrder.createdAt, "yyyy-MM-dd HH:mm") || "-"}
                    </TableCell>
                    <TableCell>
                      <div
                        className="flex items-center gap-2"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button asChild variant="ghost" size="sm">
                          <Link
                            to={buildInventoryWorkspacePath({
                              inventoryId: purchaseOrder.inventoryId,
                              purchaseOrderId: purchaseOrder.id,
                              focus: "purchase-orders",
                            })}
                          >
                            Workspace
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ContactDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        mode="create"
        entityLabel="vendor"
        title="Create vendor"
        description="Add a new vendor record to the admin portal."
        submitText="Create vendor"
        submittingText="Creating vendor..."
        schema={VendorDataSchema}
        onSubmit={handleCreateVendor}
        isSubmitting={createVendorMutation.isPending}
        variant="vendor"
      />

      <ContactDialog
        open={editingVendor !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingVendor(null);
          }
        }}
        mode="edit"
        entityLabel="vendor"
        title="Edit vendor"
        description="Update the selected vendor details."
        submitText="Save changes"
        submittingText="Saving changes..."
        schema={VendorDataSchema}
        contact={editingVendor}
        onSubmit={handleUpdateVendor}
        isSubmitting={updateVendorMutation.isPending}
        variant="vendor"
      />

      <AlertDialog
        open={vendorToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setVendorToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vendor</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the vendor record. Existing linked purchase
              orders will keep their stored snapshots, but the direct vendor record
              will be gone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDeleteVendor()}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PurchaseOrderSheet
        poId={selectedPoId}
        open={selectedPoId.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              next.delete("poId");
              return next;
            });
          }
        }}
      />
    </div>
  );
}
